/**
 * hCaptcha Dynamic Script Loader
 *
 * Handles loading hCaptcha script dynamically (not in index.html).
 * Uses singleton pattern to prevent multiple loads.
 * Provides utilities for executing invisible and rendering visible challenges.
 *
 * @author AmalQ Frontend Team
 * @date 30-12-2025
 */

import type { HCaptchaRenderOptions, CaptchaError, CaptchaErrorCode } from './types';
import {
  HCAPTCHA_SCRIPT_URL,
  SCRIPT_LOAD_TIMEOUT,
  DEFAULT_CHALLENGE_TIMEOUT,
  CAPTCHA_ERROR_MESSAGES,
  isRecoverableError,
  isCaptchaBypassEnabled,
} from './constants';

/** Singleton promise for script loading */
let loadPromise: Promise<void> | null = null;

/** Track if script has been loaded successfully */
let scriptLoaded = false;

/**
 * Check if hCaptcha is already loaded and available
 */
export const isHCaptchaLoaded = (): boolean => {
  return (
    typeof window !== 'undefined' &&
    typeof window.hcaptcha !== 'undefined' &&
    typeof window.hcaptcha.render === 'function'
  );
};

/**
 * Create a CaptchaError object
 */
const createCaptchaError = (
  code: CaptchaErrorCode,
  customMessage?: string
): CaptchaError => ({
  code,
  message: customMessage || CAPTCHA_ERROR_MESSAGES[code],
  recoverable: isRecoverableError(code),
});

/**
 * Load hCaptcha script dynamically
 *
 * Uses singleton pattern - calling multiple times returns the same promise.
 * Script is loaded from official hCaptcha CDN.
 *
 * @returns Promise that resolves when script is loaded
 * @throws CaptchaError if script fails to load
 */
export const loadHCaptcha = async (): Promise<void> => {
  // Already loaded
  if (isHCaptchaLoaded()) {
    return Promise.resolve();
  }

  // Return existing promise if loading in progress
  if (loadPromise) {
    return loadPromise;
  }

  loadPromise = new Promise<void>((resolve, reject) => {
    // Check if script tag already exists (from previous attempt)
    const existingScript = document.querySelector(
      `script[src^="${HCAPTCHA_SCRIPT_URL}"]`
    ) as HTMLScriptElement | null;

    if (existingScript) {
      // Script tag exists, wait for it to load
      const checkInterval = setInterval(() => {
        if (isHCaptchaLoaded()) {
          clearInterval(checkInterval);
          scriptLoaded = true;
          resolve();
        }
      }, 100);

      // Timeout after waiting
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!isHCaptchaLoaded()) {
          loadPromise = null;
          reject(createCaptchaError('LOAD_FAILED', 'hCaptcha script load timeout'));
        }
      }, SCRIPT_LOAD_TIMEOUT);
      return;
    }

    // Create and inject new script tag
    const script = document.createElement('script');
    script.src = `${HCAPTCHA_SCRIPT_URL}?render=explicit&onload=hcaptchaOnLoad`;
    script.async = true;
    script.defer = true;

    // Global callback for hCaptcha loaded
    window.hcaptchaOnLoad = () => {
      scriptLoaded = true;
      resolve();
    };

    script.onerror = () => {
      loadPromise = null;
      reject(createCaptchaError('LOAD_FAILED'));
    };

    // Timeout handler
    const timeoutId = setTimeout(() => {
      if (!scriptLoaded) {
        loadPromise = null;
        reject(createCaptchaError('LOAD_FAILED', 'hCaptcha script load timeout'));
      }
    }, SCRIPT_LOAD_TIMEOUT);

    script.onload = () => {
      clearTimeout(timeoutId);
      // Wait a bit for hCaptcha to initialize
      const checkInterval = setInterval(() => {
        if (isHCaptchaLoaded()) {
          clearInterval(checkInterval);
          scriptLoaded = true;
          resolve();
        }
      }, 50);

      // Timeout for initialization
      setTimeout(() => {
        clearInterval(checkInterval);
        if (!isHCaptchaLoaded()) {
          loadPromise = null;
          reject(createCaptchaError('LOAD_FAILED', 'hCaptcha initialization failed'));
        }
      }, 5000);
    };

    document.head.appendChild(script);
  });

  return loadPromise;
};

/**
 * Execute invisible hCaptcha challenge
 *
 * Creates a hidden container, renders invisible hCaptcha, and executes.
 * User doesn't see anything - verification happens in background.
 *
 * @param siteKey - hCaptcha site key from backend
 * @param timeout - Timeout in milliseconds (default: 30 seconds)
 * @returns Promise with hCaptcha token
 * @throws CaptchaError if challenge fails
 */
export const executeInvisible = async (
  siteKey: string,
  timeout: number = DEFAULT_CHALLENGE_TIMEOUT
): Promise<string> => {
  // Bypass mode for testing
  if (isCaptchaBypassEnabled()) {
    return `bypass_token_${Date.now()}`;
  }

  await loadHCaptcha();

  const hcaptcha = window.hcaptcha;
  if (!hcaptcha) {
    throw createCaptchaError('LOAD_FAILED', 'hCaptcha not available after load');
  }

  return new Promise<string>((resolve, reject) => {
    let timeoutId: ReturnType<typeof setTimeout>;
    let widgetId: string | null = null;

    // Create hidden container for invisible widget
    const container = document.createElement('div');
    container.id = `hcaptcha-invisible-${Date.now()}`;
    container.style.cssText = 'position:absolute;left:-9999px;top:-9999px;visibility:hidden;';
    document.body.appendChild(container);

    // Cleanup function
    const cleanup = () => {
      clearTimeout(timeoutId);
      try {
        if (widgetId !== null) {
          hcaptcha.reset(widgetId);
          hcaptcha.remove(widgetId);
        }
      } catch {
        // Ignore cleanup errors
      }
      container.remove();
    };

    try {
      widgetId = hcaptcha.render(container.id, {
        sitekey: siteKey,
        size: 'invisible',
        callback: (token: string) => {
          cleanup();
          resolve(token);
        },
        'expired-callback': () => {
          cleanup();
          reject(createCaptchaError('TOKEN_EXPIRED'));
        },
        'error-callback': () => {
          cleanup();
          reject(createCaptchaError('EXECUTION_FAILED'));
        },
      });

      // Set timeout
      timeoutId = setTimeout(() => {
        cleanup();
        reject(createCaptchaError('TIMEOUT'));
      }, timeout);

      // Execute the invisible challenge
      // hCaptcha.execute() returns Promise only with { async: true }
      // Without it, it may return undefined and use callbacks instead
      const executeResult = hcaptcha.execute(widgetId, { async: true });
      if (executeResult && typeof executeResult.catch === 'function') {
        executeResult.catch(() => {
          cleanup();
          reject(createCaptchaError('EXECUTION_FAILED'));
        });
      }
    } catch (error) {
      cleanup();
      reject(
        createCaptchaError(
          'EXECUTION_FAILED',
          error instanceof Error ? error.message : 'Failed to execute invisible CAPTCHA'
        )
      );
    }
  });
};

/**
 * Render visible hCaptcha widget
 *
 * Renders the hCaptcha widget in the specified container.
 * User must interact with the widget to complete verification.
 *
 * @param containerId - DOM element ID for widget container
 * @param siteKey - hCaptcha site key
 * @param callbacks - Callback functions for widget events
 * @param options - Optional widget configuration
 * @returns Widget ID for cleanup
 */
export const renderVisible = async (
  containerId: string,
  siteKey: string,
  callbacks: {
    onVerify: (token: string) => void;
    onError: () => void;
    onExpire: () => void;
    onClose?: () => void;
  },
  options?: {
    theme?: 'light' | 'dark';
    size?: 'normal' | 'compact';
    language?: string;
  }
): Promise<string> => {
  // Bypass mode for testing - immediately call onVerify with mock token
  if (isCaptchaBypassEnabled()) {
    setTimeout(() => {
      callbacks.onVerify(`bypass_token_${Date.now()}`);
    }, 500);
    return 'bypass_widget_id';
  }

  await loadHCaptcha();

  const hcaptcha = window.hcaptcha;
  if (!hcaptcha) {
    throw createCaptchaError('LOAD_FAILED', 'hCaptcha not available after load');
  }

  const renderOptions: HCaptchaRenderOptions = {
    sitekey: siteKey,
    size: options?.size || 'normal',
    theme: options?.theme || 'light',
    hl: options?.language,
    callback: callbacks.onVerify,
    'expired-callback': callbacks.onExpire,
    'error-callback': callbacks.onError,
    'close-callback': callbacks.onClose,
  };

  const widgetId = hcaptcha.render(containerId, renderOptions);
  return widgetId;
};

/**
 * Reset a widget
 *
 * @param widgetId - Widget ID from render
 */
export const resetWidget = (widgetId: string): void => {
  if (isHCaptchaLoaded() && window.hcaptcha) {
    try {
      window.hcaptcha.reset(widgetId);
    } catch {
      // Widget may have already been removed
    }
  }
};

/**
 * Remove a widget
 *
 * @param widgetId - Widget ID from render
 */
export const removeWidget = (widgetId: string): void => {
  if (isHCaptchaLoaded() && window.hcaptcha) {
    try {
      window.hcaptcha.remove(widgetId);
    } catch {
      // Widget may have already been removed
    }
  }
};

/**
 * Get response token from a widget
 *
 * @param widgetId - Widget ID from render
 * @returns Token string or empty string if not available
 */
export const getWidgetResponse = (widgetId?: string): string => {
  if (isHCaptchaLoaded() && window.hcaptcha) {
    try {
      return window.hcaptcha.getResponse(widgetId);
    } catch {
      return '';
    }
  }
  return '';
};
